/**
 * The phone crew switcher — a bottom sheet, not a dropdown.
 * `design/GT Mobile Crew.dc.html` (the sheet markup) with its data computed in
 * `design/Games Tonight Mobile.dc.html` (the shell). The chip that opens it
 * lives in `MobileApp.tsx`.
 *
 * WHY A SHEET AND NOT THE DESKTOP MENU: the phone column has no hamburger and
 * no nav drawer — its navigation is the five-item bottom tab bar — so there is
 * nowhere to hang a dropdown. A bottom sheet is this column's idiom for
 * anything that would be a desktop menu, which is also why the crew rides
 * beside the wordmark rather than inside a drawer.
 *
 * The phone carries a second header line the desktop menu does not:
 * `NOTHING CROSSES CREWS`. It is in the markup and in no changelog entry; the
 * markup is newer, so it ships.
 */

import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { switcherVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

export default function CrewSheet(): JSX.Element {
  const s = useQd();
  const v = switcherVM(s);

  return (
    <Sheet
      label="Party switcher sheet"
      maxHeight="80vh"
      onClose={s.closeSw}
      header={{ title: 'YOUR PARTIES', titleClr: 'var(--gt-text)', titleFs: '8px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: '8.5px',
            letterSpacing: '.08em',
            color: 'var(--gt-muted-2)',
            padding: '10px 14px 6px',
          }}
        >
          NOTHING CROSSES PARTIES
        </div>

        {v.rows.map((c) => (
          <div
            key={c.key}
            onClick={() => {
              // Twin of the desktop dropdown row: a tap that lands mid-switch
              // is dropped by the store, so it must not look accepted here.
              if (v.busy) return;
              s.closeSw();
              if (!c.on) void s.switchCrew(c.key);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '13px 14px',
              borderTop: '1px solid var(--gt-line-0)',
              background: c.bg,
              cursor: v.busy ? 'progress' : 'pointer',
              minHeight: '52px',
              boxSizing: 'border-box',
            }}
          >
            <span style={{ fontFamily: MONO, fontSize: '11.5px', color: c.clr, flex: 1, minWidth: 0 }}>
              {c.nameUp}
            </span>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-muted-2)' }}>
              {c.membersTxt}
            </span>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: c.roleClr }}>
              {c.roleTxt}
            </span>
          </div>
        ))}

        <div
          onClick={() => {
            if (v.busy) return;
            s.closeSw();
            s.openWizard();
          }}
          style={{
            padding: '15px 14px 18px',
            borderTop: '1px solid var(--gt-line-0)',
            fontFamily: MONO,
            fontSize: '10px',
            letterSpacing: '.04em',
            color: v.busy ? 'var(--gt-muted)' : 'var(--acc)',
            cursor: v.busy ? 'progress' : 'pointer',
          }}
        >
          + START ANOTHER PARTY
        </div>
      </div>
    </Sheet>
  );
}
